import type { FormikProps } from "formik";
import type { FC } from "react";
import { Form } from "react-bootstrap";
import {
	ContentType,
	type CreatePostDto,
	TIKTOK_BRANDED_PRIVATE_TOOLTIP,
	TIKTOK_DISCLOSURE_REQUIRED_TOOLTIP,
	tiktokCommercialLabelPrompt,
	tiktokConsentText,
	tiktokPrivacyLabel,
} from "shared";
import { ExtraForm } from "../common/pure/ExtraFormGroup";

type Props = {
	form: FormikProps<CreatePostDto>;
	privacyOptions: string[];
	creatorName?: string;
	commentDisabled: boolean;
	duetDisabled: boolean;
	stitchDisabled: boolean;
	/** Photo posts hide Duet/Stitch per TikTok guidelines. */
	isPhotoPost: boolean;
};

export const TikTokPostSettings: FC<Props> = ({
	form,
	privacyOptions,
	creatorName,
	commentDisabled,
	duetDisabled,
	stitchDisabled,
	isPhotoPost,
}) => {
	const privacyLevel = form.values.tiktokPrivacyLevel ?? "";
	const disclosureOn = form.values.tiktokCommercialDisclosure === true;
	const brandOrganic = form.values.tiktokBrandOrganic === true;
	const brandedContent = form.values.tiktokBrandedContent === true;
	const isPrivate = privacyLevel === "SELF_ONLY";

	const allowCommentChecked =
		!commentDisabled && form.values.tiktokAllowComment === true;
	const allowDuetChecked =
		!duetDisabled && form.values.tiktokAllowDuet === true;
	const allowStitchChecked =
		!stitchDisabled && form.values.tiktokAllowStitch === true;

	const settings = {
		privacyLevel: privacyLevel || undefined,
		allowComment: allowCommentChecked,
		allowDuet: allowDuetChecked,
		allowStitch: allowStitchChecked,
		commercialDisclosure: disclosureOn,
		brandOrganic,
		brandedContent,
	};

	const labelPrompt = tiktokCommercialLabelPrompt(settings, isPhotoPost);
	const consent = tiktokConsentText(settings);
	const disclosureIncomplete = disclosureOn && !brandOrganic && !brandedContent;

	const setPrivacy = (value: string) => {
		void form.setFieldValue("tiktokPrivacyLevel", value || undefined);
		if (value === "SELF_ONLY" && brandedContent) {
			void form.setFieldValue("tiktokBrandedContent", false);
		}
	};

	const toggleDisclosure = (checked: boolean) => {
		void form.setFieldValue("tiktokCommercialDisclosure", checked);
		if (!checked) {
			void form.setFieldValue("tiktokBrandOrganic", false);
			void form.setFieldValue("tiktokBrandedContent", false);
		}
	};

	const toggleBrandedContent = (checked: boolean) => {
		if (checked && isPrivate) {
			return;
		}
		void form.setFieldValue("tiktokBrandedContent", checked);
	};

	return (
		<fieldset className="mt-3 border-0 p-0 m-0 min-w-0">
			<legend className="visually-hidden">TikTok post settings</legend>
			{creatorName && (
				<Form.Text className="text-muted d-block mb-2">
					Posting to TikTok as <strong>{creatorName}</strong>
				</Form.Text>
			)}

			<ExtraForm.Group label="TikTok privacy" required>
				<ExtraForm.Select
					{...form.getFieldProps("tiktokPrivacyLevel")}
					value={privacyLevel}
					onChange={(e) => setPrivacy(e.target.value)}
				>
					<option value="">Select privacy</option>
					{privacyOptions.map((level) => {
						const disabledByBranded = brandedContent && level === "SELF_ONLY";
						return (
							<option
								key={level}
								value={level}
								disabled={disabledByBranded}
								title={
									disabledByBranded ? TIKTOK_BRANDED_PRIVATE_TOOLTIP : undefined
								}
							>
								{tiktokPrivacyLabel(level)}
							</option>
						);
					})}
				</ExtraForm.Select>
				{brandedContent && (
					<Form.Text className="text-muted d-block mt-1">
						{TIKTOK_BRANDED_PRIVATE_TOOLTIP}
					</Form.Text>
				)}
				{privacyOptions.length === 1 && privacyOptions[0] === "SELF_ONLY" && (
					<Form.Text className="text-muted d-block mt-1">
						TikTok only allows Only me until the app passes Direct Post audit.
					</Form.Text>
				)}
			</ExtraForm.Group>

			<ExtraForm.Group label="Interaction settings" className="mt-3">
				<Form.Check
					type="checkbox"
					id="tiktok-allow-comment"
					label="Allow Comment"
					checked={allowCommentChecked}
					disabled={commentDisabled}
					title={
						commentDisabled
							? "Comments are disabled in your TikTok account settings"
							: undefined
					}
					onChange={(e) =>
						void form.setFieldValue("tiktokAllowComment", e.target.checked)
					}
				/>
				{!isPhotoPost && (
					<>
						<Form.Check
							type="checkbox"
							id="tiktok-allow-duet"
							label="Allow Duet"
							checked={allowDuetChecked}
							disabled={duetDisabled}
							title={
								duetDisabled
									? "Duets are disabled in your TikTok account settings"
									: undefined
							}
							onChange={(e) =>
								void form.setFieldValue("tiktokAllowDuet", e.target.checked)
							}
						/>
						<Form.Check
							type="checkbox"
							id="tiktok-allow-stitch"
							label="Allow Stitch"
							checked={allowStitchChecked}
							disabled={stitchDisabled}
							title={
								stitchDisabled
									? "Stitches are disabled in your TikTok account settings"
									: undefined
							}
							onChange={(e) =>
								void form.setFieldValue("tiktokAllowStitch", e.target.checked)
							}
						/>
					</>
				)}
				<Form.Text className="text-muted d-block mt-1">
					Interaction options start off. Turn on the ones you want to allow.
				</Form.Text>
			</ExtraForm.Group>

			<ExtraForm.Group label="Content disclosure" className="mt-3">
				<Form.Check
					type="switch"
					id="tiktok-commercial-disclosure"
					label="Indicate whether this content promotes yourself, a brand, product or service"
					checked={disclosureOn}
					onChange={(e) => toggleDisclosure(e.target.checked)}
				/>
				{disclosureOn && (
					<div className="ms-1 mt-2">
						<Form.Check
							type="checkbox"
							id="tiktok-your-brand"
							label="Your brand"
							checked={brandOrganic}
							onChange={(e) =>
								void form.setFieldValue("tiktokBrandOrganic", e.target.checked)
							}
						/>
						<Form.Text className="text-muted d-block mb-2">
							You are promoting yourself or your own business.
						</Form.Text>
						<span
							title={isPrivate ? TIKTOK_BRANDED_PRIVATE_TOOLTIP : undefined}
						>
							<Form.Check
								type="checkbox"
								id="tiktok-branded-content"
								label="Branded content"
								checked={brandedContent}
								disabled={isPrivate}
								onChange={(e) => toggleBrandedContent(e.target.checked)}
							/>
						</span>
						<Form.Text className="text-muted d-block">
							{isPrivate
								? "Visibility for branded content can't be private."
								: "You are promoting another brand or a third party."}
						</Form.Text>
						{labelPrompt && (
							<Form.Text className="d-block mt-2 fw-semibold">
								{labelPrompt}
							</Form.Text>
						)}
						{disclosureIncomplete && (
							<Form.Text
								className="text-danger d-block mt-2"
								title={TIKTOK_DISCLOSURE_REQUIRED_TOOLTIP}
							>
								{TIKTOK_DISCLOSURE_REQUIRED_TOOLTIP}
							</Form.Text>
						)}
					</div>
				)}
			</ExtraForm.Group>

			<Form.Text className="d-block mt-3 small">{consent}</Form.Text>
		</fieldset>
	);
};

export const isTikTokPhotoSelection = (
	files: Array<{ type?: string }>,
): boolean =>
	files.length > 0 && files.every((f) => f.type === ContentType.IMAGE);
